<template>
<div>
    <!-- SSR slot content: Blade HTML rendered for SEO, removed once Vue mounts -->
    <div v-if="showSeoContent" ref="seoContent">
        <slot></slot>
    </div>

    <!-- Search filter -->
    <div class="row mb-3">
        <div class="col-md-4">
            <el-input
                v-model="searchText"
                placeholder="Filter by token..."
                prefix-icon="el-icon-search"
                clearable
                size="default"
            />
        </div>
        <div class="col-md-4">
            <el-select v-model="sortField" placeholder="Sort by" size="default" class="w-100">
                <el-option label="Total Supplied (USD)" value="total_supply_usd" />
                <el-option label="Best Supply APY" value="best_supply_apy" />
                <el-option label="Best Borrow APY" value="best_borrow_apy" />
                <el-option label="Token" value="token_symbol" />
            </el-select>
        </div>
        <div class="col-md-4 d-flex align-items-center gap-2">
            <el-switch v-model="showOnlyWithBorrow" active-text="Has Borrow" />
            <span class="small" style="color: #6b7280;">
                {{ filteredRates.length }} tokens
            </span>
        </div>
    </div>

    <!-- Rate comparison table -->
    <div class="table-mobile-scroll">
        <table class="compass-table compass-table--striped">
            <thead>
                <tr>
                    <th style="min-width: 100px;">Token</th>
                    <th class="text-end" style="min-width: 100px;">Best Lend</th>
                    <th class="text-end" style="min-width: 100px;">Best Borrow</th>
                    <th v-for="protocol in protocols" :key="protocol" class="text-end" style="min-width: 130px;">
                        {{ capitalize(protocol) }} <span class="small" style="color: #9ca3af;">L / B</span>
                    </th>
                    <th class="text-center" style="width: 110px;">Action</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="row in filteredRates" :key="row.token_symbol"
                    @click="selectToken(row)"
                    style="cursor: pointer;"
                    :class="{ 'table-active': selectedToken?.token_symbol === row.token_symbol }">
                    <td>
                        <strong>{{ row.token_symbol }}</strong>
                        <div class="small" style="color: #9ca3af;">
                            ${{ formatCompact(row.total_supply_usd) }} supplied
                        </div>
                    </td>
                    <td class="td-num text-end">
                        <span class="pct-change pct-positive">{{ formatApy(row.best_supply_apy) }}</span>
                        <div class="small" style="color: #9ca3af;">{{ capitalize(row.best_supply_protocol) }}</div>
                    </td>
                    <td class="td-num text-end">
                        <template v-if="row.best_borrow_apy && row.best_borrow_apy > 0">
                            <span class="font-mono-num">{{ formatApy(row.best_borrow_apy) }}</span>
                            <div class="small" style="color: #9ca3af;">{{ capitalize(row.best_borrow_protocol) }}</div>
                        </template>
                        <span v-else style="color: #9ca3af;">—</span>
                    </td>
                    <td v-for="protocol in protocols" :key="protocol" class="td-num text-end">
                        <template v-if="getProtocolRate(row, protocol)">
                            <span class="pct-change--positive">{{ formatApy(getProtocolRate(row, protocol).supply_apy) }}</span>
                            <span style="color: #6b7280;"> / </span>
                            <span v-if="getProtocolRate(row, protocol).borrow_apy > 0">
                                {{ formatApy(getProtocolRate(row, protocol).borrow_apy) }}
                            </span>
                            <span v-else style="color: #9ca3af;">—</span>
                        </template>
                        <span v-else style="color: #d1d5db;">—</span>
                    </td>
                    <td class="text-center">
                        <div class="d-flex gap-1 justify-content-center">
                            <button class="compass-btn compass-btn--sm compass-btn--primary"
                                    @click.stop="openDeposit(row)">
                                Supply
                            </button>
                            <button v-if="row.best_borrow_apy && row.best_borrow_apy > 0"
                                    class="compass-btn compass-btn--sm compass-btn--outline"
                                    @click.stop="openBorrow(row)">
                                Borrow
                            </button>
                        </div>
                    </td>
                </tr>
                <tr v-if="filteredRates.length === 0">
                    <td :colspan="4 + protocols.length" class="text-center" style="color: #9ca3af; padding: 2rem;">
                        <template v-if="loading">Loading rates...</template>
                        <template v-else>No lending rates found.</template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <!-- Deposit / Borrow Modal -->
    <lend-deposit-modal
        v-if="showDepositModal"
        :token="selectedToken"
        :protocols="protocols"
        :mode="modalMode"
        @close="showDepositModal = false"
    />
</div>
</template>

<script>
import { useProtocolRates } from '../../composables/useProtocolRates';
import { useLendFormatters } from '../../composables/useLendFormatters';

export default {
    name: 'BorrowRates',
    props: {
        initialRates: { type: Array, default: () => [] },
        protocols: { type: Array, default: () => ['kamino', 'drift', 'marginfi', 'jup-lend', 'loopscale'] },
    },
    setup() {
        const { fetchLendingRates, lendingRates, loading } = useProtocolRates();
        const { capitalize, formatApy, formatCompact } = useLendFormatters();
        return { fetchLendingRates, lendingRates, loading, capitalize, formatApy, formatCompact };
    },
    data() {
        return {
            rates: this.initialRates || [],
            searchText: '',
            sortField: 'total_supply_usd',
            showOnlyWithBorrow: false,
            selectedToken: null,
            showDepositModal: false,
            modalMode: 'supply', // 'supply' or 'borrow'
            showSeoContent: true,
        };
    },
    computed: {
        filteredRates() {
            let data = [...this.rates];

            if (this.searchText) {
                const q = this.searchText.toLowerCase();
                data = data.filter(r => r.token_symbol?.toLowerCase().includes(q));
            }

            if (this.showOnlyWithBorrow) {
                data = data.filter(r => r.best_borrow_apy && r.best_borrow_apy > 0);
            }

            data.sort((a, b) => {
                if (this.sortField === 'token_symbol') {
                    return (a.token_symbol || '').localeCompare(b.token_symbol || '');
                }
                return (b[this.sortField] || 0) - (a[this.sortField] || 0);
            });

            return data;
        },
    },
    async mounted() {
        // Remove SSR slot content — Vue interactive UI takes over
        this.showSeoContent = false;

        // Try to refresh from API in the background
        try {
            const freshRates = await this.fetchLendingRates();
            if (freshRates && freshRates.length > 0) {
                this.rates = freshRates;
            }
        } catch (e) {
            // Keep SSR data
        }
    },
    methods: {
        getProtocolRate(row, protocol) {
            if (!row.protocols) return null;
            return row.protocols.find(p => p.protocol?.toLowerCase() === protocol.toLowerCase());
        },
        // capitalize, formatApy, formatCompact provided by useLendFormatters via setup()
        selectToken(row) {
            this.selectedToken = row;
        },
        openDeposit(row) {
            this.selectedToken = row;
            this.modalMode = 'supply';
            this.showDepositModal = true;
        },
        openBorrow(row) {
            this.selectedToken = row;
            this.modalMode = 'borrow';
            this.showDepositModal = true;
        },
    },
};
</script>
